import { useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { router, useLocalSearchParams, usePathname } from 'expo-router';
import { theme } from '@/constants/theme';
import { kpLiveApi } from '@/services/kursi-play-v2/kpLiveApiClient';
import { buildRuntimeExamResultPath, buildRuntimeHomePath, resolveRuntimeBase } from '@/features/unified-learning/runtime/runtimeRoutes';
import { buildExamQueue, qualityCountIncrement, qualityToPoints, summarizeExamSession, type ExamQueueItem } from '@/features/unified-learning/runtime/examModeService';
import type { KpActionRuntime, KpQualityTier, KpSubmitScenarioActionResponse } from '@/types/kursi-play-v2';

const BG = '#0F0F1A';
const SURFACE = '#1E1B3A';
const BORDER = '#2D2B50';
const TEXT = '#FFFFFF';
const TEXT_MED = '#A0A0B8';
const PRIMARY = theme.colors.primary;

const EMPTY_COUNTS: Record<KpQualityTier, number> = { strong: 0, acceptable: 0, weak: 0, risky: 0 };

const QUALITY_LABELS: Record<string, string> = {
  strong: 'מעולה',
  acceptable: 'סביר',
  weak: 'חלש',
  risky: 'מסוכן',
};

export function RuntimeExamSessionScreen() {
  const { slug, uid, token, target } = useLocalSearchParams<{ slug: string; uid: string; token?: string; target?: string }>();
  const runtimeSession = { uid: String(uid ?? ''), token: token ? String(token) : undefined };
  const runtimeBase = resolveRuntimeBase(usePathname());

  const targetQuestions = Number(target ?? 10) || 10;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [queue, setQueue] = useState<ExamQueueItem[]>([]);
  const [idx, setIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<KpSubmitScenarioActionResponse | null>(null);

  const [qualityCounts, setQualityCounts] = useState<Record<KpQualityTier, number>>(EMPTY_COUNTS);
  const [lessonScoreMap, setLessonScoreMap] = useState<Record<string, { attempts: number; score: number }>>({});
  const [sessionXp, setSessionXp] = useState(0);
  const [answeredQuestions, setAnsweredQuestions] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!slug || !uid) {
        setError('חסרים נתונים לסשן Exam');
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const built = await buildExamQueue(String(slug), String(uid), targetQuestions);
        if (!cancelled) {
          setQueue(built.queue);
          setLoading(false);
        }
      } catch (e: unknown) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'שגיאה בטעינת Exam Mode');
          setLoading(false);
        }
      }
    };

    void load();
    return () => { cancelled = true; };
  }, [slug, uid, targetQuestions]);

  const current = queue[idx];
  const progress = queue.length > 0 ? Math.round(((idx + (result ? 1 : 0)) / queue.length) * 100) : 0;

  const chooseAction = async (action: KpActionRuntime) => {
    if (!current || submitting) return;
    setSubmitting(true);
    try {
      const response = await kpLiveApi.submitScenarioAction({
        scenario_id: current.scenario.id,
        action_id: action.id,
        lesson_id: current.lessonId,
        slug: String(slug),
        user_id: String(uid),
      });

      setResult(response);
      setSessionXp((x) => x + (response.xp_awarded ?? 0));
      setAnsweredQuestions((q) => q + 1);
      setQualityCounts((prev) => qualityCountIncrement(prev, response.quality_tier));
      setLessonScoreMap((prev) => {
        const before = prev[current.lessonId] ?? { attempts: 0, score: 0 };
        return {
          ...prev,
          [current.lessonId]: {
            attempts: before.attempts + 1,
            score: before.score + qualityToPoints(response.quality_tier),
          },
        };
      });
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'שגיאה בשליחת תשובה');
    } finally {
      setSubmitting(false);
    }
  };

  const finishExam = () => {
    const summary = summarizeExamSession({
      totalQuestions: queue.length,
      answeredQuestions,
      xpEarned: sessionXp,
      qualityCounts,
      lessonScoreMap,
    });

    router.replace(buildRuntimeExamResultPath(runtimeBase, String(slug), runtimeSession, {
      total: String(summary.totalQuestions),
      answered: String(summary.answeredQuestions),
      xp: String(summary.xpEarned),
      strong: String(summary.qualityCounts.strong),
      acceptable: String(summary.qualityCounts.acceptable),
      weak: String(summary.qualityCounts.weak),
      risky: String(summary.qualityCounts.risky),
      weak_lessons: summary.weakLessonIds.join(','),
      strong_lessons: summary.strongLessonIds.join(','),
      readiness: summary.readinessBand,
    }) as any);
  };

  const goNext = () => {
    setResult(null);
    if (idx + 1 >= queue.length) {
      finishExam();
      return;
    }
    setIdx((i) => i + 1);
  };

  const readinessHint = useMemo(() => {
    if (qualityCounts.risky >= 3) return 'זוהו החלטות מסוכנות — התמקד באזורי חולשה לאחר המבחן.';
    if (qualityCounts.strong >= 4) return 'תנופה חזקה עד כה — המשך באותו קצב.';
    return 'מצב מבחן מודד מוכנות נוכחית, לא שליטה סופית בחומר.';
  }, [qualityCounts]);

  if (loading) return <View style={s.center}><ActivityIndicator color={PRIMARY} size="large" /></View>;
  if (error) {
    return (
      <View style={s.center}>
        <Text style={s.error}>{error}</Text>
        <Pressable
          style={s.retryBtn}
          onPress={() => {
            if (router.canGoBack()) router.back();
            else router.replace(buildRuntimeHomePath(runtimeBase, String(slug), runtimeSession) as any);
          }}
        >
          <Text style={s.retryText}>חזרה</Text>
        </Pressable>
      </View>
    );
  }
  if (!current) {
    return (
      <View style={s.center}>
        <Text style={s.error}>לא נמצאו תרחישים למצב מבחן.</Text>
        <Pressable style={s.retryBtn} onPress={() => router.replace(buildRuntimeHomePath(runtimeBase, String(slug), runtimeSession) as any)}>
          <Text style={s.retryText}>חזרה לבית המסלול</Text>
        </Pressable>
      </View>
    );
  }

  return (
    <View style={s.root}>
      <View style={s.top}>
        <Text style={s.topTitle}>מצב מבחן · בדיקת מוכנות</Text>
        <Text style={s.topSub}>שאלה {idx + 1}/{queue.length} · XP {sessionXp}</Text>
      </View>
      <View style={s.progressTrack}><View style={[s.progressFill, { width: `${progress}%` as any }]} /></View>

      <ScrollView contentContainerStyle={s.content}>
        <View style={s.notice}><Text style={s.noticeText}>{readinessHint}</Text></View>

        <Text style={s.lessonLabel}>{current.lessonTitle}</Text>
        <Text style={s.scenarioTitle}>{current.scenario.title}</Text>
        <Text style={s.body}>{current.scenario.situation}</Text>
        <Text style={s.body}>{current.scenario.decision_point}</Text>

        {!result && current.scenario.actions.map((action) => (
          <Pressable key={action.id} style={s.actionBtn} onPress={() => { void chooseAction(action); }} disabled={submitting}>
            <Text style={s.actionText}>{action.label}</Text>
          </Pressable>
        ))}

        {result && result.consequence && (
          <View style={s.resultCard}>
            <Text style={s.resultTitle}>{result.consequence.outcome_label} · {QUALITY_LABELS[result.quality_tier] ?? result.quality_tier}</Text>
            <Text style={s.body}>{result.consequence.narrative}</Text>
            {result.reinforcements.slice(0, 2).map((r, ri) => (
              <View key={`${ri}-${r.headline}`} style={s.reinfRow}>
                <Text style={s.reinfTitle}>{r.headline}</Text>
                <Text style={s.reinfBody}>{r.body}</Text>
              </View>
            ))}
            <Pressable style={s.nextBtn} onPress={goNext}>
              <Text style={s.nextText}>{idx + 1 >= queue.length ? 'סיים מבחן ✓' : 'שאלה הבאה →'}</Text>
            </Pressable>
          </View>
        )}
      </ScrollView>
    </View>
  );
}

const s = StyleSheet.create({
  root: { flex: 1, backgroundColor: BG },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', backgroundColor: BG },
  top: { paddingTop: 50, paddingHorizontal: 18, paddingBottom: 8 },
  topTitle: { color: TEXT, fontFamily: theme.fonts.bold, fontSize: 16 },
  topSub: { color: TEXT_MED, fontFamily: theme.fonts.regular, fontSize: 12, marginTop: 2 },
  progressTrack: { height: 5, backgroundColor: '#2A274A' },
  progressFill: { height: 5, backgroundColor: PRIMARY },
  content: { padding: 18, gap: 10, paddingBottom: 50 },
  notice: { backgroundColor: '#141326', borderColor: BORDER, borderWidth: 1, borderRadius: 10, padding: 10 },
  noticeText: { color: TEXT_MED, fontFamily: theme.fonts.regular, fontSize: 12, textAlign: 'right' },
  lessonLabel: { color: PRIMARY, fontFamily: theme.fonts.bold, textAlign: 'right' },
  scenarioTitle: { color: TEXT, fontFamily: theme.fonts.bold, fontSize: 22, textAlign: 'right' },
  body: { color: TEXT_MED, fontFamily: theme.fonts.regular, fontSize: 14, lineHeight: 22, textAlign: 'right' },
  actionBtn: { backgroundColor: SURFACE, borderWidth: 1, borderColor: BORDER, borderRadius: 12, padding: 12 },
  actionText: { color: TEXT, fontFamily: theme.fonts.semiBold, textAlign: 'right' },
  resultCard: { marginTop: 8, backgroundColor: SURFACE, borderWidth: 1, borderColor: BORDER, borderRadius: 14, padding: 14, gap: 10 },
  resultTitle: { color: '#34D399', fontFamily: theme.fonts.bold, textAlign: 'right' },
  reinfRow: { backgroundColor: '#16142A', borderRadius: 10, padding: 10 },
  reinfTitle: { color: TEXT, fontFamily: theme.fonts.bold, fontSize: 13, textAlign: 'right' },
  reinfBody: { color: TEXT_MED, fontFamily: theme.fonts.regular, fontSize: 12, textAlign: 'right', marginTop: 4 },
  nextBtn: { backgroundColor: PRIMARY, borderRadius: 10, alignItems: 'center', paddingVertical: 11 },
  nextText: { color: '#fff', fontFamily: theme.fonts.bold },
  error: { color: '#FCA5A5', fontFamily: theme.fonts.bold, textAlign: 'center' },
  retryBtn: { backgroundColor: PRIMARY, paddingHorizontal: 20, paddingVertical: 10, borderRadius: 10, marginTop: 12 },
  retryText: { color: '#fff', fontFamily: theme.fonts.bold },
});
